export interface CreateLinkRequest {
  destination: string;
  slug?: string;
  expiration?: string;
  tags?: string[];
  password?: string;
  domainId?: string; // Custom domain ID for branded links
}

export interface CreateLinkResponse {
  id: string;
  slug: string;
  destination: string;
  shortUrl: string;
  expiration?: string;
  tags: string[];
  is_password_protected?: boolean;
  createdAt: string;
}

export interface Link {
  id: string;
  slug: string;
  destination: string;
  shortUrl: string;
  expiration?: string;
  tags: string[];
  is_password_protected?: boolean;
  createdAt: string;
  updatedAt: string;
  clicks: number;
}

export interface QRConfig {
  color: string;
  background_color: string;
  logo_url?: string;
  logo_size?: number;
  logo_position?:
    | "center"
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right";
  logo_background?: boolean;
  logo_background_color?: string;
  logo_border_radius?: number;
  size: number;
  margin: number;
  error_correction: "L" | "M" | "Q" | "H";
  format: "svg";
  pattern?: "square" | "circle" | "rounded";
  gradient?: boolean;
  gradient_type?: "linear" | "radial";
  gradient_direction?: number;
  gradient_color_start?: string;
  gradient_color_end?: string;
}

export interface CreateQRRequest {
  link_id: string;
  config: QRConfig;
}

export interface QRCode {
  id: string;
  link_id: string;
  user_id: string;
  config: QRConfig;
  created_at: string;
}

export interface ApiErrorResponse {
  error: string;
  message?: string;
}

export interface Domain {
  id: string;
  domain: string;
  verified: boolean;
  verifiedAt?: string;
  sslStatus: "pending" | "active" | "failed";
  createdAt: string;
  customHostnameId?: string;
}

export interface CreateDomainRequest {
  domain: string;
}

export interface CreateDomainResponse {
  id: string;
  domain: string;
  verified: boolean;
  verifiedAt?: string;
  sslStatus: "pending" | "active" | "failed";
  customHostnameId?: string;
  verificationInstructions: {
    type: "TXT";
    name: string;
    value: string;
  } | null;
}

export interface VerifyDomainResponse {
  verified: boolean;
  verifiedAt?: string;
  sslStatus: "pending" | "active" | "failed";
  customHostnameId?: string;
  error?: string;
  details?: {
    hostnameStatus?: string;
    sslStatus?: string;
    ownershipVerification?: {
      type: string;
      name: string;
      value: string;
    };
  };
}

export interface CustomHostnameStatusResponse {
  domain: string;
  customHostnameId?: string;
  status: "no_custom_hostname" | "pending" | "active" | "error";
  sslStatus?: "pending" | "active" | "failed";
  verified: boolean;
  certificateAuthority?: string;
  validationMethod?: string;
  error?: string;
}

export interface DeleteDomainResponse {
  success: boolean;
  customHostnameDeleted: boolean;
  customHostnameError?: string;
}

export class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(
    baseUrl: string = import.meta.env.VITE_API_URL || "https://api.go2url.dev",
  ) {
    this.baseUrl = baseUrl;
    this.token =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("go2url_token")
        : null;
  }

  setToken(token: string) {
    this.token = token;
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("go2url_token", token);
    }
  }

  clearToken() {
    this.token = null;
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("go2url_token");
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const config: RequestInit = {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    };

    if (this.token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${this.token}`,
      };
    }

    // Pass through Playwright User-Agent for e2e tests to bypass rate limiting
    if (
      typeof navigator !== "undefined" &&
      navigator.userAgent?.includes("Playwright-E2E-Tests")
    ) {
      config.headers = {
        ...config.headers,
        "User-Agent": navigator.userAgent,
      };
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(
          data.error || "Request failed",
          response.status,
          data,
        );
      }

      // Handle ResponseBuilder.success() wrapper from worker
      if (
        data &&
        typeof data === "object" &&
        data.success === true &&
        "data" in data
      ) {
        return data.data;
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError("Network error", 0, {
        error: "Failed to connect to server",
      });
    }
  }

  async createLink(linkData: CreateLinkRequest): Promise<CreateLinkResponse> {
    return this.request<CreateLinkResponse>("/links", {
      method: "POST",
      body: JSON.stringify(linkData),
    });
  }

  async getUserLinks(
    limit = 50,
    offset = 0,
  ): Promise<{ links: Link[]; pagination: any }> {
    return this.request<{ links: Link[]; pagination: any }>(
      `/links?limit=${limit}&offset=${offset}`,
    );
  }

  async updateLink(
    linkId: string,
    linkData: Partial<CreateLinkRequest>,
  ): Promise<Link> {
    return this.request<Link>(`/links/${linkId}`, {
      method: "PUT",
      body: JSON.stringify(linkData),
    });
  }

  async verifyLinkPassword(
    slug: string,
    data: { password: string },
  ): Promise<{
    success: boolean;
    data?: { destination: string; slug: string };
  }> {
    return this.request<{
      success: boolean;
      data?: { destination: string; slug: string };
    }>(`/links/${slug}/verify-password`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async deleteLink(linkId: string): Promise<void> {
    return this.request<void>(`/links/${linkId}`, {
      method: "DELETE",
    });
  }

  async register(
    email: string,
    password: string,
  ): Promise<{ message: string; email: string }> {
    return this.request<{ message: string; email: string }>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  async verifyEmail(
    token: string,
  ): Promise<{ message: string; token: string; user: any }> {
    return this.request<{ message: string; token: string; user: any }>(
      `/auth/verify-email?token=${token}`,
    );
  }

  async resendVerificationEmail(
    email: string,
  ): Promise<{ message: string; email: string }> {
    return this.request<{ message: string; email: string }>(
      "/auth/resend-verification",
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );
  }

  async login(
    email: string,
    password: string,
    rememberMe = false,
  ): Promise<{ token: string; user: any; rememberMe?: boolean }> {
    return this.request<{ token: string; user: any; rememberMe?: boolean }>(
      "/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ email, password, rememberMe }),
      },
    );
  }

  async logout(): Promise<void> {
    this.clearToken();
  }

  async getUserInfo(): Promise<{ user: any }> {
    return this.request<{ user: any }>("/auth/user");
  }

  async googleAuth(): Promise<{ token: string; user: any }> {
    return this.request<{ token: string; user: any }>("/auth/google");
  }

  async createQRCode(qrData: CreateQRRequest): Promise<QRCode> {
    return this.request<QRCode>("/qr", {
      method: "POST",
      body: JSON.stringify(qrData),
    });
  }

  async getQRCodeImage(qrId: string): Promise<Blob> {
    const url = `${this.baseUrl}/qr/${qrId}`;
    const config: RequestInit = {
      headers: {},
    };

    if (this.token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${this.token}`,
      };
    }

    // Pass through Playwright User-Agent for e2e tests to bypass rate limiting
    if (
      typeof navigator !== "undefined" &&
      navigator.userAgent?.includes("Playwright-E2E-Tests")
    ) {
      config.headers = {
        ...config.headers,
        "User-Agent": navigator.userAgent,
      };
    }

    const response = await fetch(url, config);
    if (!response.ok) {
      throw new ApiError("Failed to fetch QR code", response.status);
    }

    return response.blob();
  }

  async getQRCodeDownloadUrl(qrId: string): Promise<{ downloadUrl: string }> {
    try {
      const response = await this.request<{
        signed_url: string;
        worker_download_url?: string;
      }>(`/qr/${qrId}/signed-url`);
      // Prefer worker download URL over signed URL for better reliability
      const downloadUrl = response.worker_download_url || response.signed_url;
      return { downloadUrl };
    } catch (error) {
      // Fallback to direct worker download if signed URL generation fails
      console.warn("Signed URL generation failed, using fallback:", error);
      const downloadUrl = `${this.baseUrl}/qr/${qrId}/download`;
      return { downloadUrl };
    }
  }

  async getUserQRCodes(): Promise<QRCode[]> {
    const response = await this.request<{ qr_codes: QRCode[] }>("/qr");
    return response.qr_codes;
  }

  async updateQRCode(
    qrId: string,
    data: { config: QRConfig },
  ): Promise<QRCode> {
    return this.request<QRCode>(`/qr/${qrId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteQRCode(qrId: string): Promise<void> {
    return this.request<void>(`/qr/${qrId}`, {
      method: "DELETE",
    });
  }

  async uploadLogo(
    formData: FormData,
  ): Promise<{ url: string; filename: string }> {
    const url = `${this.baseUrl}/qr/upload-logo`;
    const config: RequestInit = {
      method: "POST",
      body: formData,
    };

    if (this.token) {
      config.headers = {
        Authorization: `Bearer ${this.token}`,
      };
    }

    // Pass through Playwright User-Agent for e2e tests to bypass rate limiting
    if (
      typeof navigator !== "undefined" &&
      navigator.userAgent?.includes("Playwright-E2E-Tests")
    ) {
      config.headers = {
        ...config.headers,
        "User-Agent": navigator.userAgent,
      };
    }

    const response = await fetch(url, config);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.error || "Failed to upload logo",
        response.status,
        errorData,
      );
    }

    return response.json();
  }

  async requestPasswordReset(
    email: string,
  ): Promise<{ message: string; email: string }> {
    return this.request<{ message: string; email: string }>(
      "/auth/forgot-password",
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );
  }

  async resetPassword(
    token: string,
    password: string,
  ): Promise<{ message: string; email: string }> {
    return this.request<{ message: string; email: string }>(
      "/auth/reset-password",
      {
        method: "POST",
        body: JSON.stringify({ token, password }),
      },
    );
  }

  // Analytics endpoints
  async getAnalyticsOverview(days: number = 30): Promise<{
    totalLinks: number;
    totalClicks: number;
    uniqueVisitors: number;
    topLinks: Array<{
      id: string;
      slug: string;
      destination: string;
      clicks: number;
      uniqueVisitors: number;
    }>;
    recentActivity: Array<{
      date: string;
      clicks: number;
      uniqueVisitors: number;
    }>;
  }> {
    return this.request<{
      totalLinks: number;
      totalClicks: number;
      uniqueVisitors: number;
      topLinks: Array<{
        id: string;
        slug: string;
        destination: string;
        clicks: number;
        uniqueVisitors: number;
      }>;
      recentActivity: Array<{
        date: string;
        clicks: number;
        uniqueVisitors: number;
      }>;
    }>(`/analytics/overview?days=${days}`);
  }

  async getAnalyticsSummary(
    days: number = 30,
    linkId?: string,
    startDate?: string,
    endDate?: string,
    compareWith?: {
      type: "previous" | "lastYear" | "custom";
      startDate?: string;
      endDate?: string;
    },
    filters?: {
      devices?: string[];
      countries?: string[];
      referrers?: string[];
    },
  ): Promise<{
    summary: {
      totalClicks: number;
      totalUniqueVisitors: number;
      totalMobileClicks: number;
      totalDesktopClicks: number;
      totalTabletClicks: number;
      topCountries: Array<{ country: string; count: number }>;
      topReferrers: Array<{ referrer: string; count: number }>;
    };
    timeSeries: Array<{
      date: string;
      clicks: number;
      uniqueVisitors: number;
      mobileClicks: number;
      desktopClicks: number;
      tabletClicks: number;
    }>;
    comparison?: {
      summary: {
        totalClicks: number;
        totalUniqueVisitors: number;
        totalMobileClicks: number;
        totalDesktopClicks: number;
        totalTabletClicks: number;
        topCountries: Array<{ country: string; count: number }>;
        topReferrers: Array<{ referrer: string; count: number }>;
      };
      timeSeries: Array<{
        date: string;
        clicks: number;
        uniqueVisitors: number;
        mobileClicks: number;
        desktopClicks: number;
        tabletClicks: number;
      }>;
      period: {
        type: "previous" | "lastYear" | "custom";
        startDate: string;
        endDate: string;
      };
    };
    period: {
      days: number;
      linkId?: string;
      startDate?: string;
      endDate?: string;
    };
  }> {
    const params = new URLSearchParams({ days: days.toString() });
    if (linkId) params.append("linkId", linkId);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    // Add comparison parameters
    if (compareWith) {
      params.append("compareWith", compareWith.type);
      if (compareWith.startDate)
        params.append("compareStartDate", compareWith.startDate);
      if (compareWith.endDate)
        params.append("compareEndDate", compareWith.endDate);
    }

    // Add filter parameters
    if (filters) {
      if (filters.devices && filters.devices.length > 0) {
        filters.devices.forEach((device) => params.append("devices", device));
      }
      if (filters.countries && filters.countries.length > 0) {
        filters.countries.forEach((country) =>
          params.append("countries", country),
        );
      }
      if (filters.referrers && filters.referrers.length > 0) {
        filters.referrers.forEach((referrer) =>
          params.append("referrers", referrer),
        );
      }
    }

    return this.request<{
      summary: {
        totalClicks: number;
        totalUniqueVisitors: number;
        totalMobileClicks: number;
        totalDesktopClicks: number;
        totalTabletClicks: number;
        topCountries: Array<{ country: string; count: number }>;
        topReferrers: Array<{ referrer: string; count: number }>;
      };
      timeSeries: Array<{
        date: string;
        clicks: number;
        uniqueVisitors: number;
        mobileClicks: number;
        desktopClicks: number;
        tabletClicks: number;
      }>;
      comparison?: {
        summary: {
          totalClicks: number;
          totalUniqueVisitors: number;
          totalMobileClicks: number;
          totalDesktopClicks: number;
          totalTabletClicks: number;
          topCountries: Array<{ country: string; count: number }>;
          topReferrers: Array<{ referrer: string; count: number }>;
        };
        timeSeries: Array<{
          date: string;
          clicks: number;
          uniqueVisitors: number;
          mobileClicks: number;
          desktopClicks: number;
          tabletClicks: number;
        }>;
        period: {
          type: "previous" | "lastYear" | "custom";
          startDate: string;
          endDate: string;
        };
      };
      period: {
        days: number;
        linkId?: string;
        startDate?: string;
        endDate?: string;
      };
    }>(`/analytics/summary?${params}`);
  }

  async getLinkAnalytics(
    linkId: string,
    days: number = 30,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    linkId: string;
    link: {
      id: string;
      slug: string;
      destination: string;
      created_at: string;
    };
    analytics: {
      raw: any[];
      summary: Array<{
        id: number;
        link_id: string;
        date: string;
        total_clicks: number;
        unique_visitors: number;
        mobile_clicks: number;
        desktop_clicks: number;
        tablet_clicks: number;
        top_countries?: string;
        top_referrers?: string;
      }>;
    };
    period: {
      days: number;
      startDate?: string;
      endDate?: string;
    };
  }> {
    const params = new URLSearchParams({ days: days.toString() });
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return this.request<{
      linkId: string;
      link: {
        id: string;
        slug: string;
        destination: string;
        created_at: string;
      };
      analytics: {
        raw: any[];
        summary: Array<{
          id: number;
          link_id: string;
          date: string;
          total_clicks: number;
          unique_visitors: number;
          mobile_clicks: number;
          desktop_clicks: number;
          tablet_clicks: number;
          top_countries?: string;
          top_referrers?: string;
        }>;
      };
      period: {
        days: number;
        startDate?: string;
        endDate?: string;
      };
    }>(`/analytics/${linkId}?${params}`);
  }

  async getUTMAnalytics(params: {
    linkId?: string;
    days?: number;
    startDate?: string;
    endDate?: string;
    compareWith?: {
      type: "previous" | "lastYear" | "custom";
      startDate?: string;
      endDate?: string;
    };
  }): Promise<{
    utm_campaigns: Array<{
      utm_source: string;
      utm_medium: string;
      utm_campaign: string;
      clicks: number;
      percentage: number;
    }>;
    utm_sources: Array<{
      source: string;
      clicks: number;
      percentage: number;
    }>;
    utm_mediums: Array<{
      medium: string;
      clicks: number;
      percentage: number;
    }>;
    total_utm_clicks: number;
    organic_clicks: number;
    comparison?: {
      utm_campaigns: Array<{
        utm_source: string;
        utm_medium: string;
        utm_campaign: string;
        clicks: number;
        percentage: number;
      }>;
      utm_sources: Array<{
        source: string;
        clicks: number;
        percentage: number;
      }>;
      utm_mediums: Array<{
        medium: string;
        clicks: number;
        percentage: number;
      }>;
      total_utm_clicks: number;
      organic_clicks: number;
      period: {
        type: "previous" | "lastYear" | "custom";
        startDate: string;
        endDate: string;
      };
    };
    period: {
      days?: number;
      linkId?: string;
      startDate?: string;
      endDate?: string;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params.days) queryParams.append("days", params.days.toString());
    if (params.linkId) queryParams.append("linkId", params.linkId);
    if (params.startDate) queryParams.append("startDate", params.startDate);
    if (params.endDate) queryParams.append("endDate", params.endDate);

    // Add comparison parameters
    if (params.compareWith) {
      queryParams.append("compareWith", params.compareWith.type);
      if (params.compareWith.startDate)
        queryParams.append("compareStartDate", params.compareWith.startDate);
      if (params.compareWith.endDate)
        queryParams.append("compareEndDate", params.compareWith.endDate);
    }

    return this.request<{
      utm_campaigns: Array<{
        utm_source: string;
        utm_medium: string;
        utm_campaign: string;
        clicks: number;
        percentage: number;
      }>;
      utm_sources: Array<{
        source: string;
        clicks: number;
        percentage: number;
      }>;
      utm_mediums: Array<{
        medium: string;
        clicks: number;
        percentage: number;
      }>;
      total_utm_clicks: number;
      organic_clicks: number;
      comparison?: {
        utm_campaigns: Array<{
          utm_source: string;
          utm_medium: string;
          utm_campaign: string;
          clicks: number;
          percentage: number;
        }>;
        utm_sources: Array<{
          source: string;
          clicks: number;
          percentage: number;
        }>;
        utm_mediums: Array<{
          medium: string;
          clicks: number;
          percentage: number;
        }>;
        total_utm_clicks: number;
        organic_clicks: number;
        period: {
          type: "previous" | "lastYear" | "custom";
          startDate: string;
          endDate: string;
        };
      };
      period: {
        days?: number;
        linkId?: string;
        startDate?: string;
        endDate?: string;
      };
    }>(`/analytics/utm?${queryParams}`);
  }

  // Domains endpoints
  async getDomains(): Promise<Domain[]> {
    const response = await this.request<{ domains: Domain[] }>("/domains");
    return response.domains;
  }

  async createDomain(data: CreateDomainRequest): Promise<CreateDomainResponse> {
    return this.request<CreateDomainResponse>("/domains", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async verifyDomain(domainId: string): Promise<VerifyDomainResponse> {
    return this.request<VerifyDomainResponse>(`/domains/${domainId}/verify`, {
      method: "POST",
    });
  }

  async getCustomHostnameStatus(
    domainId: string,
  ): Promise<CustomHostnameStatusResponse> {
    return this.request<CustomHostnameStatusResponse>(
      `/domains/${domainId}/status`,
    );
  }

  async renewSSL(
    domainId: string,
  ): Promise<{ success: boolean; message: string; sslStatus?: string }> {
    return this.request<{
      success: boolean;
      message: string;
      sslStatus?: string;
    }>(`/domains/${domainId}/renew-ssl`, {
      method: "POST",
    });
  }

  async reverifyDomain(domainId: string): Promise<VerifyDomainResponse> {
    return this.request<VerifyDomainResponse>(`/domains/${domainId}/reverify`, {
      method: "POST",
    });
  }

  async deleteDomain(domainId: string): Promise<DeleteDomainResponse> {
    return this.request<DeleteDomainResponse>(`/domains/${domainId}`, {
      method: "DELETE",
    });
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number = 0,
    public data: any = null,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isSlugTaken() {
    return this.status === 409 && this.message.includes("Slug already exists");
  }

  get isValidationError() {
    return this.status === 400;
  }

  get isAuthError() {
    return this.status === 401;
  }

  get isRateLimited() {
    return this.status === 429;
  }

  get isTierLimitExceeded() {
    return this.status === 403 && this.data?.code === "TIER_LIMIT_EXCEEDED";
  }

  get isEmailNotVerified() {
    return this.status === 403 && this.data?.code === "EMAIL_NOT_VERIFIED";
  }
}

export const apiClient = new ApiClient();
